import { z } from "zod";
import {
  scopeFilterSearchDefaults,
  scopeFilterSearchSchema,
} from "@/components/custom/resources/scope-filter";

/**
 * Typed router id for the MCP gateways list route. Centralized so the
 * various sheets, drawers, and effects that bind to this route's search
 * params agree on one string and can't silently drift.
 *
 * The list lives on the index route (trailing slash) so that `/mcp-gateways`
 * can act as a layout with a dedicated `/mcp-gateways/new` create page as a
 * sibling. Search params (and their validation) live on this index route.
 */
export const MCP_GATEWAYS_ROUTE_ID =
  "/_authenticated/orgs/$organizationId/projects/$projectId/mcp-gateways/" as const;

export const mcpGatewaysSearchSchema = z.object({
  /**
   * When set to a gateway id, the connect sheet auto-opens for the matching
   * row on the list page. Used by the gateway create flow to deep-link the
   * user straight into the connect flow after creating a gateway.
   */
  connect: z.string().catch("").default(""),
  ...scopeFilterSearchSchema.shape,
});

export type McpGatewaysSearch = z.infer<typeof mcpGatewaysSearchSchema>;

export const mcpGatewaysSearchDefaults: McpGatewaysSearch = {
  connect: "",
  ...scopeFilterSearchDefaults,
};

/** Which engine client scopes the edit-page detail fetch and save. */
const mcpGatewayEditBindingSchema = z.enum([
  "project",
  "tenant",
  "static",
  "account",
]);

export const mcpGatewayEditSearchSchema = z.object({
  binding: mcpGatewayEditBindingSchema.catch("project").default("project"),
});

export type McpGatewayEditSearch = z.infer<typeof mcpGatewayEditSearchSchema>;

export const mcpGatewayEditSearchDefaults: McpGatewayEditSearch = {
  binding: "project",
};

/** Deep-link pre-selected tools onto the create page (replaces list `?tools=`). */
export const mcpGatewayNewSearchSchema = z.object({
  tools: z.array(z.string()).catch([]).default([]),
});

export type McpGatewayNewSearch = z.infer<typeof mcpGatewayNewSearchSchema>;

export const mcpGatewayNewSearchDefaults: McpGatewayNewSearch = {
  tools: [],
};
